import { motion } from 'framer-motion';
import { CheckCircle, Clock, Tag } from 'lucide-react';
import React from 'react';
import { Service } from '../../../shared/hooks/useServiceSelection';

// Define default colors to use if not provided
const defaultColors = {
  cardBackground: "#000000F2",
        cardBorder: "#DDB88919",
        cardBorderSelected: "#22c55e49",
        cardShadow: "rgba(221,184,137,0.07)",
        cardShadowSelected: "rgba(34,197,94,0.25)",
        titleText: "#DDB889",
        subtitleText: "#9CA3AF",
        infoText: "#DDB889",
        iconColor: "#DDB889",
        selectionBackground: "#22c55e",
        selectionIcon: "#FFFFFF",
        badgeBackground: "#000000F2",
        badgeBorder: "#DDB88919",
        buttonBackground: "#DDB889",
        buttonText: "#000000",
        placeholderGradientFrom: "#DDB88933",
        placeholderGradientTo: "#DDB8890D",
        placeholderIconBackground: "#DDB88933"
};

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15
    }
  }
};

interface ServiceListProps {
  services: Service[];
  selectedServices: Service[];
  onToggle: (service: Service) => void;
  formatDuration: (minutes: number) => string;
  currency: { symbol: string };
  colors?: Partial<typeof defaultColors>;
  showPrices?: boolean;
  showDuration?: boolean;
}

export const ServiceList: React.FC<ServiceListProps> = ({
  services,
  selectedServices,
  onToggle,
  formatDuration,
  currency,
  colors = {},
  showPrices = true,
  showDuration = true
}) => {
  // Merge provided colors with defaults
  const themeColors = { ...defaultColors, ...colors };

  // Check if a service is selected
  const isServiceSelected = (service: Service) => {
    return selectedServices.some(s => s.id === service.id);
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="lp-w-full lp-flex lp-flex-col lp-gap-2"
    >
      {services.map((service) => (
        <motion.div
          key={service.id}
          variants={itemVariants}
          className="lp-flex lp-items-center lp-gap-3 lp-p-3 lp-rounded-xl lp-border lp-transition-all lp-duration-300 lp-cursor-pointer"
          style={{
            backgroundColor: themeColors.cardBackground,
            borderColor: isServiceSelected(service) ? themeColors.cardBorderSelected : themeColors.cardBorder,
            boxShadow: isServiceSelected(service)
              ? `0 0 15px ${themeColors.cardShadowSelected}`
              : `0 0 15px ${themeColors.cardShadow}`
          }}
          onClick={() => onToggle(service)}
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
        >
          {/* Selection Circle */}
          <div className="lp-flex-shrink-0">
            <motion.div
              className="lp-w-10 lp-h-10 lp-rounded-full lp-flex lp-items-center lp-justify-center"
              style={{
                backgroundColor: isServiceSelected(service)
                  ? themeColors.selectionBackground
                  : 'transparent',
                border: isServiceSelected(service)
                  ? 'none'
                  : `1px solid ${themeColors.cardBorder}`
              }}
              whileHover={{ rotate: isServiceSelected(service) ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              {isServiceSelected(service) ? (
                <CheckCircle
                  className="lp-w-5 lp-h-5"
                  style={{ color: themeColors.selectionIcon }}
                />
              ) : (
                <Tag
                  className="lp-w-5 lp-h-5"
                  style={{ color: themeColors.iconColor }}
                />
              )}
            </motion.div>
          </div>

          {/* Service Name & Description */}
          <div className="lp-flex-grow lp-min-w-0">
            <h4
              className="lp-text-base lp-font-medium lp-truncate"
              style={{ color: themeColors.titleText }}
            >
              {service.name}
            </h4>
            <p
              className="lp-text-xs lp-truncate"
              style={{ color: themeColors.subtitleText }}
            >
              {service.description}
            </p>
          </div>

          {/* Service Info */}
          <div className="lp-flex-shrink-0 lp-flex lp-items-center lp-gap-4">
            {showDuration && (
              <div className="lp-flex lp-items-center lp-gap-1">
                <Clock className="lp-w-3 lp-h-3" style={{ color: themeColors.iconColor }} />
                <span className="lp-text-xs" style={{ color: themeColors.infoText }}>
                  {formatDuration(service.duration)}
                </span>
              </div>
            )}

            {showPrices && (
              <div className="lp-px-3 lp-py-1 lp-rounded-lg lp-flex lp-items-center"
                style={{
                  backgroundColor: themeColors.badgeBackground,
                  border: `1px solid ${themeColors.badgeBorder}`
                }}
              >
                <span className="lp-text-xs lp-font-medium" style={{ color: themeColors.infoText }}>
                  {currency.symbol}{service.price}
                </span>
              </div>
            )}
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
};

export default ServiceList;
